import {
  Parser as HtmlToReactParser,
  ProcessNodeDefinitions,
} from "html-to-react";
import React, { Fragment } from "react";
import { ExtensionTrivia } from "~/components/annotate/chunks/extension-trivia";
import HighlightWrapper from "~/components/annotate/chunks/highlight-wrapper";
import Paragraph from "~/components/annotate/chunks/paragraph";
import TimestampBox from "~/components/annotate/chunks/timestamp-box";
import {
  IHtmlHighlight,
  InlineTriviaExtension,
  PopoverConfiguration,
} from "~/typings/annotate/annotation";

interface INode {
  name: string;
  data: string;
  parent: { attribs: any };
  attribs: any;
}

const processNodeDefinitions = ProcessNodeDefinitions();

export function injectHighlight(
  contentAsHtml: string,
  highlights: IHtmlHighlight[],
  popoverConfiguration: PopoverConfiguration,
  trackMetric: (name: string, options?: { [key: string]: any }) => void,
  articleID: string,
) {
  const htmlToReactParser = HtmlToReactParser();
  return htmlToReactParser.parseWithInstructions(
    contentAsHtml || "",
    () => true,
    buildProcessingInstructions(
      highlights,
      popoverConfiguration,
      trackMetric,
      articleID,
    ),
  );
}

const buildProcessingInstructions = (
  highlights: IHtmlHighlight[],
  popoverConfiguration: PopoverConfiguration,
  trackMetric: (name: string, options?: { [key: string]: any }) => void,
  articleID: string,
) => {
  return [
    htmlAndBodyProcessor,
    timestampProcessor,
    {
      shouldProcessNode: function (node: INode) {
        // return false;
        return node.attribs && node.attribs["data-block"];
      },
      processNode: function (node: INode, children: any, index: number) {
        //TODO: That's where we'll add our content extensions
        // return processNodeDefinitions.processDefaultNode(node, children, index);
        const blockID = node.attribs["data-block"];
        const currentExtensions = popoverConfiguration.extensions?.filter(
          (x) => x.afterBlockIndex === parseInt(blockID),
        );
        if (
          !currentExtensions ||
          !currentExtensions.length ||
          popoverConfiguration.focusMode
        ) {
          return processNodeDefinitions.processDefaultNode(
            node,
            children,
            index,
          );
        }

        return (
          <Fragment key={"block_" + blockID}>
            {processNodeDefinitions.processDefaultNode(node, children, index)}
            {currentExtensions.map((x, i) => {
              switch (x.__typename) {
                case "TriviaExtension":
                  return (
                    <ExtensionTrivia
                      key={x.__typename + "" + i}
                      extension={x as InlineTriviaExtension}
                    />
                  );
              }
              return null;
            })}
          </Fragment>
        );
      },
    },
    {
      shouldProcessNode: function (node: INode) {
        return node.attribs && node.attribs["data-index"];
      },
      processNode: function (node: INode, children: any, index: number) {
        const nodeIndex = parseInt(node.attribs["data-index"], 10);
        const nodeDuration = parseFloat(node.attribs["data-duration"]);
        const nodeStart = parseFloat(node.attribs["data-start"]);

        const highlightsInNode = highlights.filter((h) => {
          if (h.startParentID === undefined || h.endParentID === undefined) {
            console.error(
              "buildProcessingInstructions.processNode: invalid highlight detected",
              h,
            );
            return 0;
          }
          const startParentID = h.startParentID;
          const endParentID = h.endParentID;
          return nodeIndex >= startParentID && nodeIndex <= endParentID;
        });
        const attribs = { ...node.attribs, ref: undefined };

        if (highlightsInNode.length > 0) {
          let text = "";
          if (children.length) {
            text = children[0];
          }
          /* There are 4 possibilities:
           * - The highlights starts before, and end before
           * - The highlights starts in this node, and ends in another
           * - The highlight ends in this node, and starts in another
           * - The highlight starts and end in this node
           * But for now, we want a character to be in one highlight.
           * We'll process them in order they are given, and compute the start and stop
           * of each of them within this chunk without them overlapping.
           * For example, let's imagine a chunk with the following highlights:
           * - 1) Starts at char 12, ends at char 24
           * - 2) Starts at char 15, ends at char 36
           * - 3) Starts at char 4, ends at char 13
           * The following highlights would be shown:
           * - 1) starts 14 end 14
           * - 2) starts 15 ends 36
           * - 3) starts 4 ends 13
           */

          // Reversing the highlights to make it easier to manage.
          highlightsInNode.reverse();

          // Compute the range for each shown highlight
          const highlightsPerCharacter = highlightsInNode.reduce(
            (computed, highlight) => {
              if (
                highlight.startParentID === undefined ||
                highlight.endParentID === undefined ||
                highlight.endOffset === undefined ||
                highlight.startOffset === undefined
              ) {
                console.error(
                  "buildProcessingInstructions.processNode: invalid highlight detected",
                  highlight,
                );
                return computed;
              }
              const startParentID = highlight.startParentID;
              const startOffset =
                startParentID === nodeIndex ? highlight.startOffset : 0;
              const endParentID = highlight.endParentID;
              const endOffset =
                endParentID === nodeIndex ? highlight.endOffset : text.length;

              const next = [...computed];
              for (let i = startOffset; i < endOffset; i++) {
                if (next[i]) {
                  // Don't see the highlight if already assigned to this character
                  continue;
                }
                next[i] = highlight;
              }
              return next;
            },
            Array(text.length).fill(null),
          );

          // With this, we can now apply the highlights wne they change into the children, which will
          // only contains the current text
          const newChildren = [];
          let currentText = "";
          let spanCount = 0;
          let previousHighlight = null;
          for (let i = 0; i < text.length; i++) {
            if (highlightsPerCharacter[i] === previousHighlight) {
              currentText = `${currentText}${text[i]}`;
              // As long as it hasn't changed, iterate
              continue;
            }

            // The highlight has changed, we need to add a children at this point
            newChildren.push(
              createChild(
                nodeIndex,
                i,
                currentText,
                previousHighlight,
                spanCount,
                popoverConfiguration,
                trackMetric,
                articleID,
              ),
            );
            currentText = `${text[i]}`;
            previousHighlight = highlightsPerCharacter[i];
            spanCount++;
          }
          newChildren.push(
            createChild(
              nodeIndex,
              text.length,
              currentText,
              previousHighlight,
              spanCount,
              popoverConfiguration,
              trackMetric,
              articleID,
            ),
          );
          return (
            <Paragraph
              index={nodeIndex}
              duration={nodeDuration}
              start={nodeStart}
              Component={node.name}
              {...attribs}
            >
              {newChildren}
            </Paragraph>
          );
        }
        return (
          <Paragraph
            index={nodeIndex}
            duration={nodeDuration}
            start={nodeStart}
            Component={node.name}
            {...attribs}
          >
            {children}
          </Paragraph>
        );
      },
    },
    linkProcessor,
    tableProcessor,
    defaultProcessor,
  ];
};

const createChild = (
  index: number,
  offset: number,
  currentText: string,
  previousHighlight: IHtmlHighlight,
  spanCount: number,
  popoverConfiguration: PopoverConfiguration,
  trackMetric: (name: string, options?: { [name: string]: any }) => void,
  articleID: string,
) => {
  if (previousHighlight) {
    return (
      <HighlightWrapper
        dataOffset={offset - currentText.length}
        dataIndex={index}
        key={spanCount}
        highlight={previousHighlight}
        popoverConfiguration={popoverConfiguration}
        trackMetric={trackMetric}
        articleID={articleID}
      >
        {currentText}
      </HighlightWrapper>
    );
  }
  return React.createElement(
    "span",
    { "data-offset": offset - currentText.length, key: spanCount },
    currentText,
  );
};
const defaultProcessor = {
  // Anything else
  shouldProcessNode: function (node: INode) {
    return true;
  },
  processNode: function (node: INode, children: any, index: number) {
    node.attribs = { ...node.attribs, ref: undefined };
    return processNodeDefinitions.processDefaultNode(node, children, index);
  },
};

const tableProcessor = {
  shouldProcessNode: function (node: INode) {
    return node.name === "table";
  },
  processNode: function (node: INode, children: any, index: number) {
    node.attribs = {
      ...node.attribs,
      width: undefined,
      height: undefined,
      style: undefined,
    };
    return processNodeDefinitions.processDefaultNode(node, children, index);
  },
};

const linkProcessor = {
  shouldProcessNode: function (node: INode) {
    return node.name === "a";
  },
  processNode: function (node: INode, children: any, index: number) {
    node.attribs = { ...node.attribs, target: "_blank" };
    return processNodeDefinitions.processDefaultNode(node, children, index);
  },
};

const timestampProcessor = {
  shouldProcessNode: function (node: INode) {
    return node.attribs && node.attribs["data-timestamp"];
  },
  processNode: function (node: INode, children: any, index: number) {
    const timestamp = node.attribs["data-timestamp"];
    return <TimestampBox timestamp={timestamp} />;
  },
};

const htmlAndBodyProcessor = {
  shouldProcessNode: (node: INode) => {
    return node.name === "html" || node.name === "body" || node.name === "head";
  },
  processNode: function (node: INode, children: any, index: number) {
    return children;
  },
};
